<template>
    <div class="quotes-carousel">
        <div class="flex items-center justify-between mb-6">
            <h2 class="text-3xl font-bold text-foxy-text">
                Цитати
            </h2>

            <div v-if="showNavigation" class="flex items-center space-x-3">
                <button
                    :class="[
                        'rounded-full border border-foxy-border bg-foxy-elevated p-2 text-foxy-text shadow-sm transition flex items-center justify-center hover:border-foxy-action hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20',
                        { 'opacity-50 cursor-not-allowed': isBeginning }
                    ]"
                    type="button"
                    aria-label="Попередня цитата"
                    :disabled="isBeginning"
                    @click="goPrev"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <button
                    :class="[
                        'rounded-full border border-foxy-border bg-foxy-elevated p-2 text-foxy-text shadow-sm transition flex items-center justify-center hover:border-foxy-action hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20',
                        { 'opacity-50 cursor-not-allowed': isEnd }
                    ]"
                    type="button"
                    aria-label="Наступна цитата"
                    :disabled="isEnd"
                    @click="goNext"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>

        <div class="relative bg-transparent">
            <div
                class="overflow-hidden quote-viewport"
                ref="viewport"
                @click.capture="onTrackClick"
                @pointerdown="onPointerDown"
                @pointermove="onPointerMove"
                @pointerup="onPointerUp"
                @pointercancel="onPointerCancel"
                @pointerleave="onPointerLeave"
            >
                <div
                    class="flex gap-6"
                    :style="trackStyle"
                    ref="track"
                >
                    <div
                        v-for="quote in quotes"
                        :key="quote.id"
                        class="flex-shrink-0 w-[260px] sm:w-72 md:w-80 lg:w-96"
                    >
                        <div
                            class="foxy-panel !bg-foxy-elevated rounded-2xl p-6 h-full flex flex-col shadow-sm transition-colors hover:border-foxy-action/40"
                        >
                            <div class="text-foxy-action/45 dark:text-foxy-action/70 mb-4" aria-hidden="true">
                                <svg width="32" height="14" viewBox="0 0 32 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.9682 0.131836C8.63086 0.131836 10.0929 0.533169 11.3542 1.33584C12.6729 2.08117 13.4755 3.05584 13.7622 4.25983C14.3355 6.38117 13.9342 8.44517 12.5582 10.4518C11.2395 12.4012 9.4622 13.2898 7.2262 13.1178C8.71686 12.2578 9.3762 11.1972 9.2042 9.93583C8.45886 10.1078 7.71353 10.1938 6.9682 10.1938C5.01886 10.1938 3.3562 9.7065 1.9802 8.73183C0.661531 7.75717 0.00219727 6.58184 0.00219727 5.20584C0.00219727 3.7725 0.661531 2.5685 1.9802 1.59383C3.3562 0.619168 5.01886 0.131836 6.9682 0.131836ZM30.8762 4.25983C31.4495 6.38117 31.0769 8.44517 29.7582 10.4518C28.4395 12.4012 26.6335 13.2898 24.3402 13.1178C25.8309 12.2578 26.4902 11.1972 26.3182 9.93583C25.5729 10.1078 24.8275 10.1938 24.0822 10.1938C22.1329 10.1938 20.4702 9.7065 19.0942 8.73183C17.7755 7.75717 17.1162 6.58184 17.1162 5.20584C17.1162 3.7725 17.7755 2.5685 19.0942 1.59383C20.4702 0.619168 22.1329 0.131836 24.0822 0.131836C25.7449 0.131836 27.2069 0.533169 28.4682 1.33584C29.7869 2.08117 30.5895 3.05584 30.8762 4.25983Z" fill="currentColor"></path></svg>
                            </div>
                            <div class="mb-4">
                                <template v-if="quote.book && quote.book.slug">
                                    <a
                                        :href="`/books/${quote.book.slug}`"
                                        class="text-foxy-text-secondary text-sm hover:text-foxy-action transition-colors truncate"
                                    >
                                        {{ quote.book.title || 'Без назви книги' }}
                                    </a>
                                </template>
                                <template v-else>
                                    <p class="text-foxy-text-secondary text-sm truncate">
                                        {{ quote.book?.title || 'Без назви книги' }}
                                    </p>
                                </template>
                            </div>
                            <p ref="quoteBodies" class="text-foxy-text text-lg italic leading-8 featured-quote-clamp whitespace-pre-line break-words mb-3 flex-1">{{ quote.content }}</p>
                            <div class="mb-4 min-h-[1.25rem]">
                                <button v-if="isQuoteTruncated(quote.id)" type="button"
                                    class="text-sm font-semibold text-foxy-action hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm"
                                    @click="openFullQuote(quote, $event)">Читати повністю</button>
                            </div>
                            <div class="flex items-center justify-between mt-auto">
                                <div class="flex items-start space-x-3">
                                    <template v-if="quote.user && quote.user.username">
                                        <a :href="`/users/${quote.user.username}`" class="inline-flex items-center space-x-3 group">
                                            <user-avatar
                                                :src="quote.user?.avatar_display || quote.user?.avatar_url || quote.user?.avatar || ''"
                                                :name="quote.user.name || quote.user.username"
                                                size="md"
                                                class="transition-transform duration-200 group-hover:scale-110"
                                            />
                                            <div class="min-w-0">
                                                <p class="text-foxy-text font-semibold group-hover:text-foxy-action transition-colors truncate">
                                                    {{ quote.user.name }}
                                                </p>
                                                <p class="text-foxy-text-secondary text-sm group-hover:text-foxy-action transition-colors truncate">
                                                    {{ '@' + quote.user.username }}
                                                </p>
                                            </div>
                                        </a>
                                    </template>
                                    <template v-else>
                                        <user-avatar
                                            :name="quote.user?.name || 'Анонімний автор'"
                                            size="md"
                                            guest
                                        />
                                        <div class="min-w-0">
                                            <p class="text-foxy-text font-semibold truncate">
                                                {{ quote.user?.name || 'Анонімний автор' }}
                                            </p>
                                        </div>
                                    </template>
                                </div>

                                <div v-if="Number(quote.likes_count) > 0" class="flex items-center space-x-2 text-foxy-action">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <span class="text-sm text-foxy-text-secondary">
                                        {{ quote.likes_count }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="py-12 text-center text-foxy-text-secondary">
                Завантаження цитат...
            </div>
            <div v-else-if="!quotes.length" class="py-12 text-center text-foxy-text-secondary">
                Поки що немає цитат
            </div>
        </div>
        <div v-if="activeQuote" class="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-3 sm:p-6"
            @click.self="closeFullQuote">
            <section ref="quoteDialog" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1"
                class="foxy-panel w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl p-5 sm:p-8">
                <div class="flex items-center justify-between gap-4 mb-4">
                    <h3 :id="dialogTitleId" class="text-xl font-bold text-foxy-text">Цитата</h3>
                    <button ref="quoteDialogClose" type="button" aria-label="Закрити цитату"
                        class="shrink-0 rounded-lg px-3 py-2 text-foxy-text focus-visible:ring-2 focus-visible:ring-foxy-action"
                        @click="closeFullQuote">Закрити</button>
                </div>
                <p class="whitespace-pre-line break-words text-lg italic leading-8 text-foxy-text">{{ activeQuote.content }}</p>
                <a v-if="activeQuote.book && activeQuote.book.slug" :href="`/books/${activeQuote.book.slug}`"
                    class="mt-5 inline-block text-sm font-semibold text-foxy-action hover:underline">Перейти до книги</a>
            </section>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import UserAvatar from '../UserAvatar.vue';

export default {
    name: 'FeaturedQuotesCarousel',
    components: {
        UserAvatar,
    },
    props: {
        initialQuotes: {
            type: Array,
        },
        endpoint: {
            type: String,
            default: '/api/quotes/featured',
        },
        limit: {
            type: Number,
            default: 10,
        },
    },
    data() {
        return {
            quotes: Array.isArray(this.initialQuotes) ? [...this.initialQuotes] : [],
            isLoading: false,
            error: null,
            currentIndex: 0,
            cardWidth: 0,
            gap: 24,
            viewportWidth: 0,
            currentOffset: 0,
            isDragging: false,
            isPointerActive: false,
            hasDragged: false,
            dragStartX: 0,
            dragStartOffset: 0,
            suppressTransition: false,
            truncatedQuoteIds: {},
            activeQuote: null,
            returnFocusElement: null,
            bodyHadOverflow: false,
            suppressClickUntil: 0,
        };
    },
    computed: {
        dialogTitleId() {
            return `featured-quote-dialog-title-${this._uid}`;
        },
        visibleCount() {
            if (!this.cardWidth) {
                return 1;
            }
            const step = this.cardWidth + this.gap;
            if (step <= 0) {
                return 1;
            }
            return Math.max(1, Math.floor((this.viewportWidth + this.gap) / step));
        },
        maxIndex() {
            return Math.max(0, this.quotes.length - this.visibleCount);
        },
        isBeginning() {
            return this.currentIndex <= 0;
        },
        isEnd() {
            return this.currentIndex >= this.maxIndex;
        },
        showNavigation() {
            return this.quotes.length > this.visibleCount;
        },
        showPagination() {
            return this.pageCount > 1;
        },
        pageCount() {
            return Math.max(1, this.maxIndex + 1);
        },
        trackStyle() {
            return {
                transform: `translateX(-${this.currentOffset}px)`
                    ,
                transition: this.isDragging || this.suppressTransition
                    ? 'none'
                    : 'transform 0.5s ease-out',
            };
        },
    },
    watch: {
        quotes() {
            this.updateMeasurements();
        },
    },
    mounted() {
        if (Array.isArray(this.initialQuotes)) {
            this.updateMeasurements();
        } else {
            this.fetchQuotes();
        }
        window.addEventListener('resize', this.handleResize);
        document.addEventListener('keydown', this.handleKeydown);
    },
    beforeDestroy() {
        this.closeFullQuote();
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('keydown', this.handleKeydown);
    },
    methods: {
        async fetchQuotes() {
            this.isLoading = true;
            this.error = null;
            try {
                const response = await axios.get(this.endpoint, {
                    params: {
                        limit: this.limit,
                    },
                });
                this.quotes = response.data?.data ?? [];
                this.$nextTick(() => {
                    this.updateMeasurements();
                });
            } catch (error) {
                console.error('Failed to load featured quotes', error);
                this.error = 'Не вдалося завантажити цитати. Спробуйте пізніше.';
            } finally {
                this.isLoading = false;
            }
        },
        updateMeasurements() {
            this.$nextTick(() => {
                const viewport = this.$refs.viewport;
                const track = this.$refs.track;
                if (!viewport || !track || !track.children.length) {
                    return;
                }

                this.measureQuoteOverflow();
                this.viewportWidth = viewport.clientWidth;
                const firstSlide = track.children[0];
                this.cardWidth = firstSlide.offsetWidth;

                const style = window.getComputedStyle(track);
                const gap = parseFloat(style.columnGap || style.gap || '0');
                this.gap = Number.isFinite(gap) ? gap : 0;

                const clampedIndex = Math.min(this.currentIndex, this.maxIndex);
                this.currentIndex = clampedIndex;

                this.suppressTransition = true;
                this.setOffset(clampedIndex * (this.cardWidth + this.gap));
                this.$nextTick(() => {
                    this.suppressTransition = false;
                });
            });
        },
        getScrollableDistance() {
            const viewport = this.$refs.viewport;
            const track = this.$refs.track;
            if (!viewport || !track) {
                return 0;
            }
            return Math.max(0, track.scrollWidth - viewport.clientWidth);
        },
        setOffset(offset) {
            const max = this.getScrollableDistance();
            this.currentOffset = Math.max(0, Math.min(offset, max));
        },
        goToIndex(index) {
            const clamped = Math.max(0, Math.min(index, this.maxIndex));
            this.currentIndex = clamped;
            const step = this.cardWidth + this.gap;
            this.setOffset(step > 0 ? clamped * step : 0);
        },
        goPrev() {
            this.goToIndex(this.currentIndex - 1);
        },
        goNext() {
            this.goToIndex(this.currentIndex + 1);
        },
        goToPage(pageIndex) {
            this.goToIndex(pageIndex);
        },
        onPointerDown(event) {
            if (event.pointerType === 'mouse' && event.button !== 0) {
                return;
            }
            this.isPointerActive = true;
            this.hasDragged = false;
            this.isDragging = false;
            this.dragStartX = event.clientX;
            this.dragStartOffset = this.currentOffset;
            this.suppressTransition = false;
        },
        onPointerMove(event) {
            if (!this.isPointerActive) {
                return;
            }
            const delta = event.clientX - this.dragStartX;
            if (!this.hasDragged && Math.abs(delta) > 6) {
                this.hasDragged = true;
                this.isDragging = true;
                this.suppressTransition = true;
            }
            if (!this.hasDragged) {
                return;
            }
            event.preventDefault();
            this.setOffset(this.dragStartOffset - delta);
        },
        finishDrag(event) {
            if (!this.isPointerActive) {
                return;
            }
            this.isPointerActive = false;
            const wasDragging = this.hasDragged;
            this.hasDragged = false;
            this.isDragging = false;
            this.suppressTransition = false;

            if (wasDragging) {
                this.suppressClickUntil = Date.now() + 350;
                const step = this.cardWidth + this.gap;
                if (step > 0) {
                    const index = Math.round(this.currentOffset / step);
                    this.goToIndex(index);
                } else {
                    this.goToIndex(0);
                }
            }
        },
        onPointerUp(event) {
            this.finishDrag(event);
        },
        onPointerCancel(event) {
            this.finishDrag(event);
        },
        onPointerLeave(event) {
            this.finishDrag(event);
        },
        handleResize() {
            this.updateMeasurements();
        },
        onTrackClick(event) {
            if (Date.now() < this.suppressClickUntil) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        measureQuoteOverflow() {
            this.$nextTick(() => {
                const bodies = Array.isArray(this.$refs.quoteBodies)
                    ? this.$refs.quoteBodies : (this.$refs.quoteBodies ? [this.$refs.quoteBodies] : []);
                const truncated = {};
                bodies.forEach((body, index) => {
                    const quote = this.quotes[index];
                    if (quote) truncated[quote.id] = body.scrollHeight > body.clientHeight + 1;
                });
                this.truncatedQuoteIds = truncated;
            });
        },
        isQuoteTruncated(quoteId) {
            return Boolean(this.truncatedQuoteIds[quoteId]);
        },
        openFullQuote(quote, event) {
            if (!this.isQuoteTruncated(quote.id)) return;
            this.returnFocusElement = event?.currentTarget || document.activeElement;
            this.bodyHadOverflow = document.body.classList.contains('overflow-hidden');
            this.activeQuote = quote;
            document.body.classList.add('overflow-hidden');
            this.$nextTick(() => this.$refs.quoteDialogClose?.focus());
        },
        closeFullQuote() {
            if (!this.activeQuote) return;
            this.activeQuote = null;
            if (!this.bodyHadOverflow) document.body.classList.remove('overflow-hidden');
            const returnFocus = this.returnFocusElement;
            this.returnFocusElement = null;
            this.$nextTick(() => {
                if (returnFocus?.isConnected) returnFocus.focus();
            });
        },
        handleKeydown(event) {
            if (!this.activeQuote) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                this.closeFullQuote();
            } else if (event.key === 'Tab') {
                const controls = [...this.$refs.quoteDialog.querySelectorAll('button, a[href]')];
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (!this.$refs.quoteDialog.contains(document.activeElement)) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus();
                    return;
                }
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        },
    },
};
</script>


<style scoped>
.featured-quote-clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    overflow: hidden;
    max-height: 8rem;
}
.quote-viewport {
    touch-action: pan-y pinch-zoom;
}
</style>
